import React from "react";
import { Box, Paper, Typography, useTheme } from "@mui/material";
import InfoGridSkeleton from "components/Shared/Skeleton/InfoGridSkeleton";
import { localizeDateTimeShort } from "utils/dateFormats";

const GeneralLedgerBasicDetail = ({ generalLedgerDetail, isLoading }) => {
    const theme = useTheme();

    if (isLoading) return <InfoGridSkeleton />;

    const gl = generalLedgerDetail;
    const debit = gl?.debitAmount || 0;
    const credit = gl?.creditAmount || 0;
    const isBalanced = Math.abs(debit - credit) < 0.01;
    const diff = Math.abs(debit - credit);

    const fmt = (n) =>
        n?.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });

    const shipmentId = Array.isArray(gl?.shipment)
        ? gl.shipment[0]?.shipmentId
        : gl?.shipment?.shipmentId;

    const debits = gl?.debits || [];
    const credits = gl?.credits || [];

    const greenBg = theme.palette.mode === "dark" ? "rgba(102,187,106,0.16)" : "rgba(46,125,50,0.1)";
    const greenColor = theme.palette.mode === "dark" ? "#81c784" : "#2e7d32";
    const redBg = theme.palette.mode === "dark" ? "rgba(244,67,54,0.16)" : "rgba(211,47,47,0.1)";
    const redColor = theme.palette.mode === "dark" ? "#e57373" : "#d32f2f";

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Paper
                elevation={0}
                sx={{
                    width: "100%",
                    backgroundColor: "background.paper",
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 3,
                    overflow: "hidden",
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        p: "20px 24px 18px",
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                    }}
                >
                    <Box>
                        <Typography variant="overline" sx={{ color: "text.secondary", letterSpacing: "0.1em" }}>
                            General Ledger Info
                        </Typography>
                        <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 500, letterSpacing: "-0.5px" }}>
                            {gl?.glNumber || "N/A"}
                        </Typography>
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                        <Box
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                px: 1.5, py: 0.5,
                                borderRadius: 4,
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                backgroundColor: isBalanced ? greenBg : redBg,
                                color: isBalanced ? greenColor : redColor,
                                border: `1px solid ${isBalanced
                                    ? theme.palette.mode === "dark" ? "rgba(102,187,106,0.3)" : "rgba(46,125,50,0.3)"
                                    : theme.palette.mode === "dark" ? "rgba(244,67,54,0.3)" : "rgba(211,47,47,0.3)"}`,
                            }}
                        >
                            {isBalanced ? (<><CheckIcon color={greenColor} /> Balanced</>) : "Unbalanced"}
                        </Box>
                        <Typography variant="caption" display="block" sx={{ color: "text.secondary", mt: 1 }}>
                            {gl?.createdAt ? localizeDateTimeShort(gl.createdAt) : "N/A"}
                        </Typography>
                    </Box>
                </Box>

                {/* Meta */}
                <Box
                    sx={{
                        p: "18px 24px",
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        rowGap: 2,
                        columnGap: 4,
                    }}
                >
                    <MetaItem label="Event (Transaction)" value={gl?.events} />
                    <MetaItem label="Service payer" value={gl?.serviceChargePayer} />
                    <MetaItem label="Shipment" value={shipmentId || "######"} mono />
                    <MetaItem label="COD Transfer" value={gl?.codTransfer?.codTransferId || "######"} mono />
                    <MetaItem label="COC collection" value={gl?.cocCollectionId?.cocCollectionId || "######"} mono />
                </Box>

                {/* Debit lines — multiple */}
                <EntrySection
                    lines={debits}
                    label="Debit"
                    sign="+"
                    iconBg={greenBg}
                    iconColor={greenColor}
                    amountColor={greenColor}
                    fmt={fmt}
                    theme={theme}
                    isPlus
                />

                {/* Credit lines — multiple */}
                <EntrySection
                    lines={credits}
                    label="Credit"
                    sign="−"
                    iconBg={redBg}
                    iconColor={redColor}
                    amountColor={redColor}
                    fmt={fmt}
                    theme={theme}
                    isPlus={false}
                />

                {/* Footer */}
                <Box
                    sx={{
                        p: "14px 24px",
                        backgroundColor: theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Net difference
                    </Typography>
                    <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, color: isBalanced ? greenColor : redColor }}
                    >
                        NPR {fmt(diff)}
                    </Typography>
                </Box>
            </Paper>
        </Box>
    );
};

// ─── Entry Section — handles multiple debit or credit lines ──────────────────

const EntrySection = ({
    lines, label, sign, iconBg, iconColor, amountColor, fmt, theme, isPlus,
}) => (
    <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
        {lines.map((line, idx) => (
            <Box
                key={idx}
                sx={{
                    p: "14px 24px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    // ✅ Divider between multiple lines — not after last
                    borderBottom: idx < lines.length - 1
                        ? `0.5px dashed ${theme.palette.divider}`
                        : "none",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                        sx={{
                            width: 32, height: 32,
                            borderRadius: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: iconBg,
                        }}
                    >
                        {isPlus
                            ? <PlusIcon color={iconColor} />
                            : <MinusIcon color={iconColor} />
                        }
                    </Box>
                    <Box>
                        <Typography
                            variant="caption"
                            sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.07em" }}
                        >
                            {/* Show "Debit 1", "Debit 2" if multiple lines */}
                            {lines.length > 1 ? `${label} ${idx + 1}` : label}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {line.account || "N/A"}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                    <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.07em" }}
                    >
                        Amount
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: amountColor }}>
                        {sign} NPR {fmt(line.amount)}
                    </Typography>
                </Box>
            </Box>
        ))}
    </Box>
);

// ─── Icons ────────────────────────────────────────────────────────────────────

const PlusIcon = ({ color }) => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M7 2v10M2 7h10" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
    </svg>
);

const MinusIcon = ({ color }) => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M2 7h10" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
    </svg>
);

const CheckIcon = ({ color }) => (
    <svg width="10" height="10" viewBox="0 0 10 10" style={{ marginRight: 4 }}>
        <circle cx="5" cy="5" r="4" fill="none" stroke={color} strokeWidth="1.5" />
        <path d="M3 5l1.3 1.5L7 3.5" stroke={color} strokeWidth="1.5" fill="none"
            strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// ─── MetaItem ─────────────────────────────────────────────────────────────────

const MetaItem = ({ label, value, mono = false }) => (
    <Box>
        <Typography
            variant="caption"
            sx={{
                color: "text.secondary",
                textTransform: "uppercase",
                letterSpacing: "0.07em",
                display: "block",
                mb: 0.5,
            }}
        >
            {label}
        </Typography>
        <Typography
            variant="body2"
            sx={{
                color: "text.primary",
                ...(mono && { fontFamily: "monospace", color: "info.main" }),
            }}
        >
            {value || "N/A"}
        </Typography>
    </Box>
);

export default GeneralLedgerBasicDetail;